'use client';
import React, { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { clearStoredRole, SET_PASSWORD_PATH } from '@/lib/roleSession';
import { useSessionCache } from '@/lib/session/sessionCache';

interface RouteGuardProps {
  children: React.ReactNode;
  requireStaff?: boolean;
}

const STAFF_PREFIXES = ['/admin-console', '/admin', '/portal/admin'];

// /mentorship-sessions, /implementation-tracker and /action-tracker are shared with members
// (their staff views are role-gated inside the pages), so only the session check applies there.
export function isStaffPath(pathname: string): boolean {
  const p = pathname || '';
  return STAFF_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
}

export function useStaffAccess(): boolean | null {
  const s = useSessionCache();
  if (!s.ready) return null;
  return s.signedIn && s.isMentorOrAdmin;
}

function Spinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white" role="status" aria-live="polite">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#581C32]" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

function useNoIndexMeta() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    meta.setAttribute('data-route-guard', 'true');
    document.querySelectorAll('meta[name="robots"]').forEach((el) => el.parentNode?.removeChild(el));
    document.head.appendChild(meta);
    return () => {
      meta.parentNode?.removeChild(meta);
    };
  }, []);
}

export default function RouteGuard({ children, requireStaff }: RouteGuardProps) {
  const router = useRouter();
  const pathname = usePathname() || '/';
  const session = useSessionCache();
  const needsStaff = requireStaff ?? isStaffPath(pathname);

  useNoIndexMeta();

  // Decision derived from the cached results: no network calls on page changes.
  const decision: 'wait' | 'login' | 'password' | 'dashboard' | 'allow' = !session.ready ?'wait'
    : !session.signedIn
      ? 'login'
      : session.mustSetPassword
        ? 'password'
        : needsStaff && !session.isMentorOrAdmin
          ? 'dashboard' :'allow';

  useEffect(() => {
    if (decision === 'login') {
      clearStoredRole();
      const search = typeof window !== 'undefined' ? window.location.search : '';
      router.replace(`/login?next=${encodeURIComponent(`${pathname}${search}`)}`);
    } else if (decision === 'password') {
      router.replace(SET_PASSWORD_PATH);
    } else if (decision === 'dashboard') {
      router.replace('/dashboard');
    }
  }, [decision, pathname, router]);

  if (decision !== 'allow') return <Spinner />;
  return <>{children}</>;
}
